import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import { formatPercentage } from '@luminovo/frontend-utils';
import { styled } from '@mui/material';
import { Pie, PieChart as RechartsPieChart, Sector } from 'recharts';

interface Datum {}

export type PieChartProps<T extends Datum> = {
    data: T[];
    getLabel: (datum: T) => string;
    getValue: (datum: T) => number;
    getColor: (datum: T) => string;
    isSelected?: (datum: T) => boolean;
    onSegmentClick?: (datum: T) => void;
};

const SIZE = 160;
const THICKNESS = 20;
const CORNER_RADIUS = 3;
// Degrees between neighbouring segments.
const PADDING_ANGLE = 1;
const DIMMED_OPACITY = 0.3;

const notSelected = () => false;

/**
 * A pie with a legend beside it that lists each datum's value and share of the total.
 */
export function PieChart<T extends Datum>({
    data,
    getLabel,
    getValue,
    getColor,
    isSelected = notSelected,
    onSegmentClick,
}: PieChartProps<T>): JSX.Element {
    const total = data.reduce((sum, datum) => sum + getValue(datum), 0);
    const hasSelection = data.some(isSelected);
    const isDimmed = (datum: T) => hasSelection && !isSelected(datum);
    const segments = data.map((datum) => ({ value: getValue(datum), datum }));

    return (
        <Flexbox gap="32px" sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <PieWrapper isClickable={Boolean(onSegmentClick)}>
                <RechartsPieChart
                    width={SIZE}
                    height={SIZE}
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    // The pie has no tooltip, so a tab stop on it would lead nowhere.
                    accessibilityLayer={false}
                >
                    <Pie
                        data={segments}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        outerRadius={SIZE / 2}
                        innerRadius={SIZE / 2 - THICKNESS}
                        paddingAngle={PADDING_ANGLE}
                        cornerRadius={CORNER_RADIUS}
                        isAnimationActive={false}
                        rootTabIndex={-1}
                        onClick={(_, index) => {
                            const segment = segments[index];
                            if (segment) {
                                onSegmentClick?.(segment.datum);
                            }
                        }}
                        shape={(props) => {
                            const segment = segments[props.index];
                            return (
                                <Sector
                                    {...props}
                                    fill={segment ? getColor(segment.datum) : props.fill}
                                    opacity={segment && isDimmed(segment.datum) ? DIMMED_OPACITY : 1}
                                />
                            );
                        }}
                    />
                </RechartsPieChart>
            </PieWrapper>
            <Flexbox gap="8px" sx={{ flexDirection: 'column', flex: 1 }}>
                {data.map((datum) => {
                    const share = total > 0 ? getValue(datum) / total : 0;
                    const selected = isSelected(datum);
                    return (
                        <LegendRow
                            key={getLabel(datum)}
                            isClickable={Boolean(onSegmentClick)}
                            isSelected={selected}
                            style={{ opacity: isDimmed(datum) ? DIMMED_OPACITY : 1 }}
                            onClick={onSegmentClick ? () => onSegmentClick(datum) : undefined}
                        >
                            <div
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 3,
                                    backgroundColor: getColor(datum),
                                    flexShrink: 0,
                                }}
                            />
                            <Text variant="body-small" color={colorSystem.neutral[8]} style={{ flex: 1 }}>
                                {getLabel(datum)}
                            </Text>
                            <Flexbox gap="4px" sx={{ justifyContent: 'flex-end' }}>
                                <Text variant="body-small" color={colorSystem.neutral[9]}>
                                    {getValue(datum)}
                                </Text>
                                <Text
                                    variant="body-small"
                                    color={colorSystem.neutral[6]}
                                    style={{ textAlign: 'right' }}
                                >
                                    {formatPercentage(share)}
                                </Text>
                            </Flexbox>
                        </LegendRow>
                    );
                })}
            </Flexbox>
        </Flexbox>
    );
}

const PieWrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'isClickable' })<{
    isClickable: boolean;
}>(({ isClickable }) => ({
    flexShrink: 0,
    width: SIZE,
    height: SIZE,
    '& .recharts-sector': {
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'opacity 0.2s ease-in-out',
    },
    // Recharts makes each sector a `<g tabindex="-1">`, so a click focuses it and the browser rings it. The keyboard
    // never reaches the sectors, and a click already shows the selection.
    '& .recharts-pie-sector:focus, & .recharts-sector:focus': {
        outline: 'none',
    },
}));

const LegendRow = styled('div', { shouldForwardProp: (prop) => prop !== 'isClickable' && prop !== 'isSelected' })<{
    isClickable: boolean;
    isSelected: boolean;
}>(({ isClickable, isSelected }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'opacity 0.2s ease-in-out',
    ...(isClickable
        ? {
              cursor: 'pointer',
              borderRadius: 4,
              padding: '4px',
              margin: '-2px -4px',
          }
        : {}),
    ...(isClickable && isSelected
        ? {
              background: colorSystem.primary[2],
              border: `1px solid ${colorSystem.primary[3]}`,
              margin: '-3px -5px',
          }
        : {}),
}));
